/**
 * CreatorDashboard.tsx
 * 
 * Dashboard for creators to manage their monetized Posits, view analytics,
 * track earnings, and manage payout settings.
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  DollarSign, 
  Users, 
  TrendingUp, 
  Eye,
  Settings,
  Plus,
  BarChart3,
  CalendarDays,
  CreditCard,
  Star,
  MessageCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
import StripeConnectOnboarding from "@/components/posit/StripeConnectOnboarding";
import DashboardLayout from "@/components/layout/DashboardLayout";

interface CreatorStats {
  total_revenue: number;
  total_subscribers: number;
  total_views: number;
  average_rating: number;
  active_posits: number;
  monthly_growth: number;
}

interface MonetizedPosit extends Tables<"posits"> {
  subscription_count?: number;
  monthly_revenue?: number;
  rating_percentage?: number;
  total_reviews?: number;
  recent_analytics?: {
    total_questions: number;
    unique_users: number;
    revenue: number;
  };
}

interface PayoutInfo {
  pending_amount: number;
  last_payout_date: string | null;
  payout_method: string;
  next_payout_date: string;
}

const CreatorDashboard: React.FC = () => {
  const [stats, setStats] = useState<CreatorStats | null>(null);
  const [posits, setPosits] = useState<MonetizedPosit[]>([]);
  const [payoutInfo, setPayoutInfo] = useState<PayoutInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch creator's monetized Posits
      const { data: positData, error: positError } = await supabase
        .from('posits')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_monetized', true)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (positError) {
        toast({
          title: "Couldn’t load Posits",
          description: "Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Enhance posits with analytics data
      const enhancedPosits = await Promise.all(
        positData.map(async (posit) => {
          // Fetch subscription count
          const { count: subscriptionCount } = await supabase
            .from('saved_posits')
            .select('*', { count: 'exact', head: true })
            .eq('posit_id', posit.id);

          // Fetch recent analytics (last 30 days)
          const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
          const { data: analytics } = await supabase
            .from('chat_history')
            .select('*')
            .eq('posit_id', posit.id)
            .gte('created_at', thirtyDaysAgo);

          const uniqueUsers = new Set(analytics?.map(a => a.user_id) || []).size;
          const totalQuestions = analytics?.length || 0;
          const revenue = posit.subscription_price ? (subscriptionCount || 0) * posit.subscription_price : 0;

          return {
            ...posit,
            subscription_count: subscriptionCount || 0,
            monthly_revenue: revenue,
            recent_analytics: {
              total_questions: totalQuestions,
              unique_users: uniqueUsers,
              revenue: revenue
            }
          };
        })
      );

      setPosits(enhancedPosits);

      // Calculate overall stats
      const totalRevenue = enhancedPosits.reduce((sum, posit) => sum + (posit.monthly_revenue || 0), 0);
      const totalSubscribers = enhancedPosits.reduce((sum, posit) => sum + (posit.subscription_count || 0), 0);
      const totalViews = enhancedPosits.reduce((sum, posit) => sum + (posit.recent_analytics?.total_questions || 0), 0);
      const activePosits = enhancedPosits.length;

      setStats({
        total_revenue: totalRevenue,
        total_subscribers: totalSubscribers,
        total_views: totalViews,
        average_rating: 4.5, // Placeholder - would need rating system
        active_posits: activePosits,
        monthly_growth: 12.5 // Placeholder - would need historical data
      });

      // Fetch payout info (placeholder - would integrate with Stripe)
      setPayoutInfo({
        pending_amount: totalRevenue * 0.7, // 70% of revenue (30% platform fee)
        last_payout_date: null,
        payout_method: "Stripe",
        next_payout_date: format(endOfMonth(new Date()), 'MMM dd, yyyy')
      });

    } catch (error) {
      toast({
        title: "Couldn’t load dashboard",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading your creator dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Creator Dashboard"
      description="Manage your monetized Posits and track your earnings"
      headerActions={
        <div className="flex gap-3">
          <Button variant="outline" asChild>
            <Link to="/create-posit">
              <Plus className="w-4 h-4 mr-2" />
              Create Posit
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/edit-profile">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Link>
          </Button>
        </div>
      }
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.total_revenue || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.monthly_growth ? (
                <span className="text-green-600">
                  +{formatPercentage(stats.monthly_growth)} from last month
                </span>
              ) : (
                "No growth data"
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subscribers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_subscribers || 0}</div>
            <p className="text-xs text-muted-foreground">
              Across {stats?.active_posits || 0} active Posits
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercentage(stats?.average_rating || 0)}</div>
            <p className="text-xs text-muted-foreground">
              Positive feedback rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_views || 0}</div>
            <p className="text-xs text-muted-foreground">
              Questions asked this month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="posits" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            My Posits
          </TabsTrigger>
          <TabsTrigger value="payouts" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payouts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Revenue Chart Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Trend</CardTitle>
              <CardDescription>Your earnings over the last 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                Chart component would go here
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest interactions with your Posits</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {posits.slice(0, 3).map((posit) => (
                  <div key={posit.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{posit.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {posit.recent_analytics?.total_questions || 0} questions this month
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(posit.monthly_revenue || 0)}</p>
                      <p className="text-sm text-muted-foreground">
                        {posit.subscription_count || 0} subscribers
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="posits" className="space-y-6">
          {/* Posits List */}
          <div className="grid gap-6">
            {posits.map((posit) => (
              <Card key={posit.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {posit.title}
                        <Badge variant={posit.privacy === 'public' ? 'default' : 'secondary'}>
                          {posit.privacy}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Created {format(new Date(posit.created_at), 'MMM dd, yyyy')}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{formatCurrency(posit.monthly_revenue || 0)}</p>
                      <p className="text-sm text-muted-foreground">Monthly revenue</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{posit.subscription_count || 0}</p>
                      <p className="text-sm text-muted-foreground">Subscribers</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{posit.recent_analytics?.total_questions || 0}</p>
                      <p className="text-sm text-muted-foreground">Questions</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{posit.recent_analytics?.unique_users || 0}</p>
                      <p className="text-sm text-muted-foreground">Users</p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/update-posit/${posit.id}`}>
                        Edit
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/chat/${posit.id}`}>
                        View Chat
                      </Link>
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={async () => {
                        try {
                          const { data, error } = await supabase.functions.invoke('posit-auto-train-invoke', {
                            body: { posit_id: posit.id, questions_requested: 5 }
                          });
                          if (error) throw error;
                          toast({
                            title: 'Self-Test started',
                            description: `${data?.created_questions?.length || 0} questions generated. Remaining quota: ${data?.remaining_quota ?? 'N/A'}`
                          });
                        } catch (e: any) {
                          toast({ title: 'Self-Test failed', description: e?.message || 'Please try again.', variant: 'destructive' });
                        }
                      }}
                    >
                      Run Self-Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="payouts" className="space-y-6">
          {/* Payout Information */}
          <Card>
            <CardHeader>
              <CardTitle>Payout Information</CardTitle>
              <CardDescription>Manage your earnings and payout settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Pending Payout</p>
                    <p className="text-2xl font-bold">{formatCurrency(payoutInfo?.pending_amount || 0)}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Next Payout Date</p>
                    <p className="text-lg">{payoutInfo?.next_payout_date || 'Not scheduled'}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium">Payout Method</p>
                  <p className="text-muted-foreground">{payoutInfo?.payout_method || 'Not configured'}</p>
                </div>

                {payoutInfo?.last_payout_date && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Last Payout</p>
                    <p className="text-muted-foreground">
                      {format(new Date(payoutInfo.last_payout_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                )}

                <div className="pt-4">
                  <StripeConnectOnboarding />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default CreatorDashboard; 